/**
 * Permission utility functions for user authorization
 */

import type { EventRead } from '@models/api';
import type { UserProfileResponse } from '@models';
import type { QuestionPermissions } from '@models/view/permissions';

/**
 * Check if a user is the creator of an event. The credited user of a calendar-synced event
 * did not make it and gets no creator rights (backend `credits_creator`).
 */
export const isEventCreator = (
  user: UserProfileResponse | null | undefined,
  event: EventRead,
): boolean => {
  if (!user?.id) return false;
  return user.id === event.creator_id && event.created_via !== 'calendar_sync';
};

/**
 * Check if a user is the creator of a question (DEPRECATED - use question.is_my_question instead)
 */
export const isQuestionCreator = (
  user: UserProfileResponse | null | undefined,
  question: QuestionPermissions | null | undefined,
): boolean => {
  // DEPRECATED: Use question.is_my_question from backend instead
  if (!user?.id || !question) return false;
  return user.id === question.user_id || user.id === question.creator_id;
};

/** Backend `is_space_admin`: an ADMIN member, or the Space's creator (who may have no member row). */
const isEventSpaceAdmin = (
  user: UserProfileResponse | null | undefined,
  event: EventRead,
): boolean =>
  event.space?.user_role === 'admin' || (!!user?.id && event.space?.creator_id === user.id);

/**
 * Check if a user can edit an event.
 * Allowed: event creator (if still a space member), space admin, or platform admin.
 */
export const canEditEvent = (
  user: UserProfileResponse | null | undefined,
  event: EventRead,
): boolean => {
  if (event.has_extra_options) return true;
  if (isEventSpaceAdmin(user, event)) return true;
  if (isEventCreator(user, event) && (!event.space || event.space.user_is_member)) return true;
  return false;
};

/**
 * Check if a user can delete an event.
 * Unlike canEditEvent, the event creator can always delete regardless of current
 * space membership. Also allowed: space admin, platform admin.
 */
export const canDeleteEvent = (
  user: UserProfileResponse | null | undefined,
  event: EventRead,
): boolean => {
  return isEventCreator(user, event) || isEventSpaceAdmin(user, event) || event.has_extra_options;
};

/**
 * Returns true when the question belongs to the current user (owner only — no admin override).
 * Relies on backend-provided `is_my_question`, not client-side creator_id comparison.
 */
export const canEditQuestion = (
  _user: UserProfileResponse | null | undefined,
  question: QuestionPermissions | null | undefined,
): boolean => {
  return Boolean(question?.is_my_question);
};

/**
 * Returns true when the question belongs to the current user (owner only — no admin override).
 * Relies on backend-provided `is_my_question`, not client-side creator_id comparison.
 */
export const canCrosspostQuestion = (
  _user: UserProfileResponse | null | undefined,
  question: QuestionPermissions | null | undefined,
): boolean => {
  return Boolean(question?.is_my_question);
};

/**
 * Check if a user can delete a question (owner or admin)
 * AIDEV-NOTE: is_my_question is true for original creator OR for users who claimed the question
 */
export const canDeleteQuestion = (
  user: UserProfileResponse | null | undefined,
  question?: QuestionPermissions | null,
): boolean => {
  if (!user || !question) return false;
  // Owner (includes claimed questions where ownership transferred) or admin
  return Boolean(question.is_my_question || question.has_extra_options);
};
